import type { FlowDto } from '@/types/dtos/flows/response/flow.dto';
import type { MediaUsageRefDto } from '@/types/dtos/media/media-playlist.dto';
import { resolveMediaPlaylistAdvanceMode } from '@/helpers/media-playlist-advance-mode.helper';
import {
  buildPlaylistMediaRows,
  findPlaylistRefForMediaItem,
  getSessionPlaylist,
  type FlowPlaylistSource,
} from '@/helpers/flow-media-playlist.helper';
import {
  resolvePlaylistForCueRow,
  collectFlowMediaById,
  resolveFlowSlotIdFromCueRow,
} from '@/helpers/simulation-cue-playability.helper';
import { resolveSessionForFlowSlot } from '@/helpers/simulation-flow.helper';
import type { SimulationCueSheetRow } from '@/types/player/simulation-cue-sheet.types';
import { getObjectId } from '@/helpers/object.id.helper';

export type ShowInfoCueRowContext = Pick<
  SimulationCueSheetRow,
  'itemId' | 'scope' | 'slotId' | 'segmentKind' | 'advanceSettings' | 'segmentAnchor'
>;

function normalizeMediaId(mediaId?: string | { id?: string } | null): string | undefined {
  if (!mediaId) {
    return undefined;
  }
  if (typeof mediaId === 'string') {
    return mediaId;
  }
  return getObjectId(mediaId as Parameters<typeof getObjectId>[0]);
}

function playlistRefMatchesMediaId(ref: Pick<MediaUsageRefDto, 'mediaId' | 'media'>, mediaId: string): boolean {
  const refMediaId = normalizeMediaId(ref.mediaId);
  const embeddedMediaId = normalizeMediaId(ref.media?.id);
  return refMediaId === mediaId || embeddedMediaId === mediaId;
}

export function resolveOnEndFromPlaylistRef(
  ref: Pick<MediaUsageRefDto, 'advanceMode'>,
  fileType?: string,
): 'advance' | 'show_info' | 'return_previous' {
  const mode = resolveMediaPlaylistAdvanceMode(ref.advanceMode, fileType);
  if (mode === 'show_info') {
    return 'show_info';
  }
  if (mode === 'return_previous') {
    return 'return_previous';
  }
  return 'advance';
}

export function isShowInfoAdvanceSettings(
  settings?: { onEnd?: 'advance' | 'show_info' | 'return_previous' } | null,
): boolean {
  return settings?.onEnd === 'show_info';
}

export function isReturnPreviousAdvanceSettings(
  settings?: { onEnd?: 'advance' | 'show_info' | 'return_previous' } | null,
): boolean {
  return settings?.onEnd === 'return_previous';
}

/** Same show_info rule as the playlist row advanceMode icon (cue sheet or live playlist ref). */
export function isSimulationShowInfoMediaRow(
  row: SimulationCueSheetRow | undefined,
  flow: FlowDto | undefined,
): boolean {
  if (!row?.itemId.startsWith('media:')) {
    return false;
  }
  if (flow) {
    return isShowInfoCueRowFromFlow(flow, row);
  }
  return isShowInfoAdvanceSettings(row.advanceSettings);
}

/** Authoritative show_info for a cue row — playlist advanceMode beats stale timeline advanceSettings. */
export function isShowInfoCueRowFromFlow(flow: FlowDto, row: ShowInfoCueRowContext): boolean {
  if (!row.itemId.startsWith('media:')) {
    return false;
  }
  const mediaId = row.itemId.slice('media:'.length);
  const playlist = resolvePlaylistForCueRow(flow, row as SimulationCueSheetRow);
  if (playlist?.refs?.length) {
    const usageRef = playlist.refs.find((ref) => playlistRefMatchesMediaId(ref, mediaId));
    if (usageRef) {
      const playlistRows = buildPlaylistMediaRows(playlist, [...collectFlowMediaById(flow).values()]);
      const playlistRow = playlistRows.find((entry) => entry.media.id === mediaId);
      if (playlistRow) {
        return (
          resolveOnEndFromPlaylistRef(playlistRow, playlistRow.media.assets?.find((a) => a.isApproved)?.fileType) ===
          'show_info'
        );
      }
      const fileType = usageRef.media?.assets?.find((asset) => asset.isApproved)?.fileType;
      if (resolveOnEndFromPlaylistRef(usageRef, fileType) === 'show_info') {
        return true;
      }
    }
  }

  const fallbackRow = findPlaylistRefForMediaItem(
    flow,
    row.itemId,
    row.scope,
    resolveFlowSlotIdFromCueRow(row as SimulationCueSheetRow),
  );
  if (!fallbackRow && row.scope !== 'session' && row.segmentKind !== 'session') {
    return false;
  }
  if (!fallbackRow) {
    const flowSlotId = resolveFlowSlotIdFromCueRow(row as SimulationCueSheetRow);
    if (flowSlotId) {
      const session = resolveSessionForFlowSlot(flow, flowSlotId);
      const sessionPlaylist = session ? getSessionPlaylist(session) : undefined;
      const sessionRow = buildPlaylistMediaRows(sessionPlaylist, session?.media ?? []).find(
        (entry) => entry.media.id === mediaId,
      );
      if (sessionRow) {
        return (
          resolveOnEndFromPlaylistRef(
            sessionRow,
            sessionRow.media.assets?.find((asset) => asset.isApproved)?.fileType,
          ) === 'show_info'
        );
      }
    }
    return false;
  }
  return (
    resolveOnEndFromPlaylistRef(fallbackRow, fallbackRow.media.assets?.find((asset) => asset.isApproved)?.fileType) ===
    'show_info'
  );
}

export function enrichSimulationRowAdvanceFromPlaylist(
  flow: FlowDto,
  row: SimulationCueSheetRow,
): SimulationCueSheetRow {
  if (!row.itemId.startsWith('media:')) {
    return row;
  }
  const playlist = resolvePlaylistForCueRow(flow, row);
  if (!playlist?.refs?.length) {
    return row;
  }
  const mediaId = row.itemId.slice('media:'.length);
  const usageRef = playlist.refs.find((ref) => playlistRefMatchesMediaId(ref, mediaId));
  if (!usageRef) {
    return row;
  }
  const mediaById = collectFlowMediaById(flow);
  const fileType =
    usageRef.media?.assets?.find((asset) => asset.isApproved)?.fileType ??
    mediaById.get(mediaId)?.assets?.find((asset) => asset.isApproved)?.fileType;
  const advanceMode = resolveMediaPlaylistAdvanceMode(usageRef.advanceMode, fileType);
  const onEnd = resolveOnEndFromPlaylistRef(usageRef, fileType);
  return {
    ...row,
    // Playlist advanceMode is authoritative — clear stale cue-sheet skip when mode is no longer `skip`
    // (e.g. operator cycled skip → return_previous; sim sheet still had operatorSkipped).
    operatorSkipped: advanceMode === 'skip',
    advanceSettings: {
      loopEnabled: usageRef.loopEnabled ?? row.advanceSettings?.loopEnabled ?? false,
      onEnd,
      ...(usageRef.dwellMs !== undefined
        ? { dwellMs: usageRef.dwellMs }
        : row.advanceSettings?.dwellMs !== undefined
          ? { dwellMs: row.advanceSettings.dwellMs }
          : {}),
    },
  };
}

export function enrichSimulationCueRowsAdvanceFromPlaylist(
  flow: FlowDto,
  rows: SimulationCueSheetRow[],
): SimulationCueSheetRow[] {
  return rows.map((row) => enrichSimulationRowAdvanceFromPlaylist(flow, row));
}

/** @deprecated Prefer isShowInfoCueRowFromFlow — kept for flow-playlist-only lookups. */
export function isShowInfoMediaItemInFlow(
  flow: FlowPlaylistSource,
  itemId: string,
  context?: {
    scope?: string;
    slotId?: string;
    segmentKind?: string;
    segmentAnchor?: string;
    advanceSettings?: SimulationCueSheetRow['advanceSettings'];
  },
): boolean {
  return isShowInfoCueRowFromFlow(flow as FlowDto, {
    itemId,
    scope: context?.scope,
    slotId: context?.slotId,
    segmentKind: context?.segmentKind ?? '',
    segmentAnchor: context?.segmentAnchor ?? '',
    advanceSettings: context?.advanceSettings,
  });
}
